import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider } from './services/contexts/AuthContext';
import Login from './components/Login';
import MFAVerification from './components/MFAVerification';
import MFASetup from './components/MFASetup';
import PasswordSetup from './components/PasswordSetup';
import Auth0Callback from './components/Auth0Callback';
import Dashboard from './components/Dashboard';
import PatientRecording from './components/PatientRecording';
import PatientRecordingNew from './components/PatientRecordingNew';
import CameraTest from './components/CameraTest';
import ProtectedRoute from './components/ProtectedRoute';

// Import persona-specific apps
import SystemAdminApp from './modules/system-admin/SystemAdminApp';
import TenantAdminApp from './modules/tenant-admin/TenantAdminApp';
import TenantDoctorApp from './modules/tenant-doctor/TenantDoctorApp';
import TenantNurseApp from './modules/tenant-nurse/TenantNurseApp';
import TenantTechnicianApp from './modules/tenant-technician/TenantTechnicianApp';
import TenantPatientApp from './modules/tenant-patient/TenantPatientApp';

import './App.css';

function App() {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || 'your-domain.auth0.com'}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || 'your_client_id'}
      authorizationParams={{
        redirect_uri: process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin + '/auth0-callback',
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email"
      }}
      cacheLocation="localstorage"
    >
      <AuthProvider>
        <div className="App">
          <Toaster position="top-right" />
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/mfa-verification" element={<MFAVerification />} />
            <Route path="/setup-password" element={<PasswordSetup />} />
            <Route path="/mfa-setup" element={<MFASetup />} />
            <Route path="/auth0-callback" element={<Auth0Callback />} />

            {/* Patient Recording Routes (public with token) */}
            <Route path="/record/:token" element={<PatientRecording />} />
            <Route path="/record_new/:token" element={<PatientRecordingNew />} />
            <Route path="/camera-test" element={<CameraTest />} />
            
            {/* Persona-based Protected Routes */}
            <Route 
              path="/sysadmin/*" 
              element={
                <ProtectedRoute roles={['SYSTEM_ADMIN']}>
                  <SystemAdminApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/tenant-admin/*" 
              element={
                <ProtectedRoute roles={['tenant_admin']}>
                  <TenantAdminApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/tenant-doctor/*" 
              element={
                <ProtectedRoute roles={['tenant_doctor']}>
                  <TenantDoctorApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/tenant-nurse/*" 
              element={
                <ProtectedRoute roles={['tenant_nurse']}>
                  <TenantNurseApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/tenant-technician/*" 
              element={
                <ProtectedRoute roles={['tenant_technician']}>
                  <TenantTechnicianApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/tenant-patient/*" 
              element={
                <ProtectedRoute roles={['tenant_patient']}>
                  <TenantPatientApp />
                </ProtectedRoute>
              } 
            />
            
            {/* Legacy Dashboard Route */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Auth0Provider>
  );
}

export default App;